import { NormalizedNumber, Percentage } from '@sparkdotfi/common-universal'
import { useState } from 'react'

import {
  BackedByChartItem,
  BackedByDetailedChartItem,
  DetailedLineChartDetailItemData,
  DetailedLineChartItemData,
  PieChartData,
} from '../types'
import { darkenColor, getPalletteColor, getTokenColorBySymbol, mapCategoryToLabel, tokenColorsBySymbol } from '../utils'
import { SavingsBackedByData } from '@/config/chain/types'
import { Token } from '@/domain/types/Token'

export interface UseBackedByChartDetailedParams {
  items: SavingsBackedByData
  underlyingToken: Token
  detailsExpanded: boolean
  onDetailsExpandedChange: (open: boolean) => void
}

export interface UseBackedByChartDetailedResult {
  items: BackedByDetailedChartItem[]
  highlightedKey: BackedByDetailedChartItem['category'] | undefined
  handleDetailsExpandedChange: (open: boolean) => void
  setHighlightedKey: (key: BackedByDetailedChartItem['category'] | undefined) => void
  handleSliceClick: (item: BackedByDetailedChartItem) => void
  ticker: string
  totalAmount: NormalizedNumber
  displayedItems: BackedByDetailedChartItem[]
  pieChartData: PieChartData
  getLineChartItemData: (item: BackedByDetailedChartItem, index: number) => DetailedLineChartItemData
  getDetailItemData: (
    detail: BackedByChartItem,
    detailIndex: number,
    parentAmount: NormalizedNumber,
  ) => DetailedLineChartDetailItemData
  getHighlightedItem: () => BackedByDetailedChartItem | undefined
  getPieSliceColor: (index: number) => string
}

function groupIntoDetailedItems(flatItems: SavingsBackedByData, underlyingToken: Token): BackedByDetailedChartItem[] {
  const usdsDetails: BackedByChartItem[] = []
  let usdsAmount = NormalizedNumber.ZERO
  let vaultTokenAmount = NormalizedNumber.ZERO

  for (const item of flatItems) {
    if (item.category === 'idle') {
      vaultTokenAmount = vaultTokenAmount.plus(item.amount)
    } else {
      usdsDetails.push({ category: item.category, amount: item.amount })
      usdsAmount = usdsAmount.plus(item.amount)
    }
  }

  const result: BackedByDetailedChartItem[] = []

  if (usdsAmount.gt(NormalizedNumber.ZERO)) {
    result.push({
      category: 'usds',
      label: 'USDS',
      amount: usdsAmount,
      details: usdsDetails,
    })
  }

  result.push({
    category: 'vault_token',
    label: underlyingToken.symbol,
    amount: vaultTokenAmount,
    details: [],
  })

  return result
}

export function useBackedByChartDetailed({
  items: flatItems,
  underlyingToken,
  detailsExpanded,
  onDetailsExpandedChange,
}: UseBackedByChartDetailedParams): UseBackedByChartDetailedResult {
  const [highlightedKey, setHighlightedKey] = useState<BackedByDetailedChartItem['category'] | undefined>(undefined)

  const items = groupIntoDetailedItems(flatItems, underlyingToken)

  const totalAmount = items.reduce((acc, item) => acc.plus(item.amount), NormalizedNumber.ZERO)
  const ticker = underlyingToken.symbol === 'ETH' || underlyingToken.symbol === 'WETH' ? 'ETH' : 'USD'

  const pieItems: BackedByDetailedChartItem[] = detailsExpanded
    ? items.flatMap((item): BackedByDetailedChartItem[] =>
        item.details.length > 0
          ? item.details.map((detail) => ({ ...detail, label: mapCategoryToLabel(detail.category), details: [] }))
          : [{ category: item.category, label: item.label, amount: item.amount, details: [] }],
      )
    : items

  const tokenColorOverrides: Record<string, string> = {
    usds: tokenColorsBySymbol.USDS,
  }
  const vaultTokenColor = getTokenColorBySymbol(underlyingToken.symbol)
  if (vaultTokenColor !== undefined) {
    tokenColorOverrides.vault_token = vaultTokenColor
  }
  const colorByCategory = pieItems.reduce<Record<string, string>>(
    (acc, item, index) => {
      if (!acc[item.category]) {
        acc[item.category] = getPalletteColor(index)
      }
      return acc
    },
    { ...tokenColorOverrides },
  )

  const displayedItems = pieItems.filter((item) =>
    totalAmount.gt(NormalizedNumber.ZERO) ? item.amount.div(totalAmount).gt(0.005) : false,
  )
  const data = displayedItems.map((item, index) => ({
    value: item.amount.toNumber(),
    color: colorByCategory[item.category] ?? getPalletteColor(index),
  }))
  const unfilteredData = pieItems.map((item, index) => ({
    value: item.amount.toNumber(),
    color: colorByCategory[item.category] ?? getPalletteColor(index),
  }))

  function handleSliceClick(item: BackedByDetailedChartItem): void {
    if (detailsExpanded) {
      onDetailsExpandedChange(false)
      setHighlightedKey(undefined)
      return
    }

    onDetailsExpandedChange(true)
    setHighlightedKey(item.category)
  }

  function handleDetailsExpandedChange(open: boolean): void {
    onDetailsExpandedChange(open)
    if (!open) {
      setHighlightedKey(undefined)
    }
  }

  function getLineChartItemData(item: BackedByDetailedChartItem, index: number): DetailedLineChartItemData {
    const percentage = totalAmount.eq(NormalizedNumber.ZERO) ? Percentage(0) : Percentage(item.amount.div(totalAmount))

    const isExpandable = item.details.length > 0
    const isExpanded = isExpandable && detailsExpanded
    const hasHighlightedChild = isExpandable && item.details.some((detail) => detail.category === highlightedKey)
    const isHighlighted = highlightedKey === item.category || hasHighlightedChild
    const baseColor = colorByCategory[item.category] ?? getPalletteColor(index)
    const color = isHighlighted ? darkenColor(baseColor) : baseColor

    return { percentage, color, isHighlighted, isExpandable, isExpanded }
  }

  function getDetailItemData(
    detail: BackedByChartItem,
    detailIndex: number,
    parentAmount: NormalizedNumber,
  ): DetailedLineChartDetailItemData {
    const percentage = parentAmount.eq(NormalizedNumber.ZERO)
      ? Percentage(0)
      : Percentage(detail.amount.div(parentAmount))
    const baseColor = colorByCategory[detail.category] ?? getPalletteColor(detailIndex)
    const isHighlighted = highlightedKey === detail.category
    const color = isHighlighted ? darkenColor(baseColor) : baseColor

    return { percentage, color, isHighlighted }
  }

  function getHighlightedItem(): BackedByDetailedChartItem | undefined {
    return highlightedKey === undefined ? undefined : displayedItems.find((item) => item.category === highlightedKey)
  }

  function getPieSliceColor(index: number): string {
    const item = displayedItems[index]
    const baseColor = item ? (colorByCategory[item.category] ?? getPalletteColor(index)) : getPalletteColor(index)
    return item && item.category === highlightedKey ? darkenColor(baseColor) : baseColor
  }

  return {
    items,
    highlightedKey,
    setHighlightedKey,
    handleDetailsExpandedChange,
    handleSliceClick,
    totalAmount,
    ticker,
    displayedItems,
    pieChartData: { data, unfilteredData },
    getLineChartItemData,
    getDetailItemData,
    getHighlightedItem,
    getPieSliceColor,
  }
}
